import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import i18n from '../i18n';
import { useTenant } from './TenantContext';
import Logo from '../components/ui/Logo';

type Theme = 'public' | 'admin';
type Language = string; // Flexible language codes (en, ar, etc.)

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    colorMode: 'light' | 'dark';
    toggleColorMode: () => void;
    language: Language;
    toggleLanguage: () => void;
    refreshTheme: () => Promise<void>;
    numberFormat: 'arabic' | 'indian';
    timeFormat: '12h' | '24h';
    dateFormat: string;
    timezone: string;
    locationLevelNames: Record<string, string>;
    primaryLangName: string;
    secondaryLangName: string;
    secondaryLangDir: 'rtl' | 'ltr';
    formatNumber: (n: number | string) => string;
    formatDate: (dateInput: Date | string | null | undefined) => string;
    formatDateTime: (dateInput: Date | string | null | undefined) => string;
    t: (key: string, ...args: any[]) => string;
    availableLanguages: { code: string, name: string, direction: 'rtl' | 'ltr' }[];
    changeLanguage: (code: string) => Promise<void>;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    customColors: {
        primary?: string;
        secondary?: string;
        fontFamily?: string;
    };
    showFloatingButtons: boolean;
    toggleFloatingButtons: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getInitialLanguage = (): Language => {
    if (typeof window !== 'undefined') {
        const savedLang = localStorage.getItem('estipana_lang');
        if (savedLang) return savedLang;
    }
    return 'primary'; // Default fallback (English)
};

const getInitialDir = (lang: string): 'rtl' | 'ltr' => {
    if (lang === 'primary' || lang === 'en') return 'ltr';
    // Look up saved dir in localStorage if we want to be perfect, but rtl is safe for 'ar'
    const savedDir = typeof window !== 'undefined' ? localStorage.getItem('estipana_dir') : null;
    return (savedDir as 'rtl' | 'ltr') || 'rtl';
};

const getInitialColorMode = (): 'light' | 'dark' => {
    if (typeof window !== 'undefined') {
        const savedMode = localStorage.getItem('estipana_color_mode');
        if (savedMode === 'dark' || savedMode === 'light') return savedMode;
    }
    return 'light';
};

// Synchronously prevent layout shift before React mounts
if (typeof window !== 'undefined') {
    const initialLang = getInitialLanguage();
    document.documentElement.dir = getInitialDir(initialLang);
    document.documentElement.lang = (initialLang === 'primary' || initialLang === 'en') ? 'en' : initialLang;

    const initialColorMode = getInitialColorMode();
    if (initialColorMode === 'dark') {
        document.documentElement.classList.add('dark');
    }
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { selectedOrgId } = useTenant();
    const queryClient = useQueryClient();
    const [theme, setTheme] = useState<Theme>('public');
    const [colorMode, setColorMode] = useState<'light' | 'dark'>(getInitialColorMode());
    const [language, setLanguage] = useState<Language>(getInitialLanguage());
    const [primaryLangName, setPrimaryLangName] = useState('English');
    const [secondaryLangName, setSecondaryLangName] = useState('العربية');
    const [secondaryLangDir, setSecondaryLangDir] = useState<'rtl' | 'ltr'>('rtl');
    const [numberFormat, setNumberFormat] = useState<'arabic' | 'indian'>('arabic');
    const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('24h');
    const [dateFormat, setDateFormat] = useState<string>('DD/MM/YYYY');
    const [timezone, setTimezone] = useState<string>('Asia/Damascus');
    const [locationLevelNames, setLocationLevelNames] = useState<Record<string, string>>({});
    const [tier, setTier] = useState<string>('individual_starter');
    const [availableLanguages, setAvailableLanguages] = useState<{ code: string, name: string, direction: 'rtl' | 'ltr' }[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [customColors, setCustomColors] = useState<{
        primary?: string;
        secondary?: string;
        fontFamily?: string;
    }>({});

    const [showFloatingButtons, setShowFloatingButtons] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('estipana_show_floating_buttons');
            return saved === null ? true : saved === 'true';
        }
        return true;
    });

    const toggleFloatingButtons = useCallback(() => {
        setShowFloatingButtons(prev => {
            const next = !prev;
            if (typeof window !== 'undefined') {
                localStorage.setItem('estipana_show_floating_buttons', String(next));
            }
            return next;
        });
    }, []);

    const t = useCallback((key: string, ...args: any[]): string => {
        return (i18n.t as any)(key, ...args);
    }, []);

    const toggleTheme = () => {
        setTheme(prev => prev === 'public' ? 'admin' : 'public');
    };

    const toggleColorMode = () => {
        setColorMode(prev => {
            const newMode = prev === 'light' ? 'dark' : 'light';
            localStorage.setItem('estipana_color_mode', newMode);
            return newMode;
        });
    };

    const changeLanguage = async (code: string) => {
        const targetCode = (code === 'primary' || code === 'en') ? 'primary' : code;

        if (targetCode === 'primary') {
            i18n.changeLanguage('en');
            setLanguage('primary');
            localStorage.setItem('estipana_lang', 'primary');
        } else if (targetCode === 'ar') {
            i18n.changeLanguage('ar');
            setLanguage('ar');
            localStorage.setItem('estipana_lang', 'ar');
            setSecondaryLangDir('rtl');
            localStorage.setItem('estipana_dir', 'rtl');
        } else {
            try {
                // If it is organization-specific custom secondary language, it's loaded in memory, no need to fetch
                if (targetCode === 'secondary') {
                    i18n.changeLanguage('secondary');
                    setLanguage('secondary');
                    localStorage.setItem('estipana_lang', 'secondary');
                    localStorage.setItem('estipana_dir', secondaryLangDir);
                } else {
                    const res = await fetch(`/api/core/organizations/translations/${targetCode}/`, { credentials: 'include' });
                    if (res.ok) {
                        const translations = await res.json();
                        i18n.addResourceBundle(targetCode, 'translation', translations, true, true);
                        i18n.changeLanguage(targetCode);
                        setLanguage(targetCode);
                        localStorage.setItem('estipana_lang', targetCode);

                        const lang = availableLanguages.find(l => l.code === targetCode);
                        if (lang) {
                            setSecondaryLangDir(lang.direction);
                            setSecondaryLangName(lang.name);
                            localStorage.setItem('estipana_dir', lang.direction);
                        }
                    }
                }
            } catch (e) {
                console.error("Failed to change language:", e);
            }
        }

        // Persist to user profile if authenticated
        const authUser = queryClient.getQueryData<any>(['auth-user']);
        if (authUser?.is_authenticated) {
            try {
                const csrfToken = document.cookie.split('estipana_csrftoken=')[1]?.split(';')[0] || '';
                fetch('/api/core/profile/update/', {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken
                    },
                    body: JSON.stringify({ language: targetCode })
                }).catch(() => { });
            } catch (e) {
                // Ignore
            }
        }
    };

    const toggleLanguage = () => {
        const isFreeTier = tier.includes('free');
        if (isFreeTier && (language === 'primary' || language === 'en') && theme !== 'public') {
            console.warn('Multi-language is a premium feature');
            return;
        }

        // Basic toggle between first available and primary
        if ((language === 'primary' || language === 'en') && availableLanguages.length > 0) {
            changeLanguage(availableLanguages[0].code);
        } else if (language === 'primary' || language === 'en') {
            changeLanguage('ar');
        } else {
            changeLanguage('primary');
        }
    };

    const [isThemeLoaded, setIsThemeLoaded] = useState(false);

    const fetchTheme = async (orgId?: number | null) => {
        try {
            const activeOrgId = orgId !== undefined ? orgId : selectedOrgId;
            const query = activeOrgId ? `?org_id=${activeOrgId}` : '';
            let res = await fetch(`/api/core/organizations/current/${query}`, { credentials: 'include' });

            // Fallback: If not logged in, try resolving by host domain to load branding before authentication
            if (!res.ok) {
                const hostname = window.location.hostname;
                res = await fetch(`/api/core/organizations/resolve_domain/?host=${hostname}`);
            }

            let data: any = null;
            if (res.ok) {
                data = await res.json();
            }

            // Determine available languages
            let langs: any[] = [{ code: 'ar', name: 'العربية', direction: 'rtl' }];
            if (data && !data.main_portal) {
                const orgLangs = data.available_languages || [];
                langs = [...orgLangs];
                // Always ensure Arabic is available as it's a built-in system language
                if (!langs.some((l: any) => l.code === 'ar')) {
                    langs.push({ code: 'ar', name: 'العربية', direction: 'rtl' });
                }
                if (data.translations && data.translations.secondary) {
                    if (!langs.some((l: any) => l.code === 'secondary')) {
                        langs = [...langs, {
                            code: 'secondary',
                            name: data.secondary_lang_name || 'العربية',
                            direction: data.secondary_lang_dir || 'rtl'
                        }];
                    }
                }
            }
            setAvailableLanguages(langs);

            if (data && data.translations) {
                if (data.translations.primary) {
                    i18n.addResourceBundle('en', 'translation', data.translations.primary, true, true);
                }
                if (data.translations.secondary) {
                    i18n.addResourceBundle('secondary', 'translation', data.translations.secondary, true, true);
                }
            }

            // Determine which language to use
            const savedLang = localStorage.getItem('estipana_lang');
            let targetLang = 'primary';

            if (savedLang) {
                // Make sure savedLang is valid for this org. Arabic and English are built-in system languages and always valid.
                if (savedLang === 'primary' || savedLang === 'en' || savedLang === 'ar' || langs.some((l: any) => l.code === savedLang)) {
                    targetLang = savedLang;
                }
            } else if (data && data.user_preferred_language) {
                targetLang = data.user_preferred_language;
            } else if (data && data.language) {
                targetLang = data.language;
            }

            // If a language is selected, fetch its translations
            if (targetLang && targetLang !== 'primary' && targetLang !== 'en' && targetLang !== 'ar') {
                if (targetLang === 'secondary' && data && data.translations?.secondary) {
                    i18n.addResourceBundle('secondary', 'translation', data.translations.secondary, true, true);
                    i18n.changeLanguage('secondary');
                } else {
                    const translationsRes = await fetch(`/api/core/organizations/translations/${targetLang}/`, { credentials: 'include' });
                    if (translationsRes.ok) {
                        const translations = await translationsRes.json();
                        i18n.addResourceBundle(targetLang, 'translation', translations, true, true);
                    }
                    i18n.changeLanguage(targetLang);
                }
            } else if (targetLang === 'ar') {
                i18n.changeLanguage('ar');
                setSecondaryLangDir('rtl');
                localStorage.setItem('estipana_dir', 'rtl');
            } else if (targetLang === 'primary' || targetLang === 'en') {
                i18n.changeLanguage('en');
            }

            if (data && !data.main_portal) {
                const isStarterTier = !data.tier || ['individual_starter', 'org_starter', 'free'].includes(data.tier);
                if (!isStarterTier && (data.primary_color || data.secondary_color || data.font_family)) {
                    setCustomColors({
                        primary: data.primary_color,
                        secondary: data.secondary_color,
                        fontFamily: data.font_family
                    });
                } else {
                    setCustomColors({});
                }
                if (data.number_format) setNumberFormat(data.number_format);
                if (data.time_format) setTimeFormat(data.time_format);
                if (data.date_format) setDateFormat(data.date_format);
                if (data.timezone) setTimezone(data.timezone);
                if (data.location_level_names) setLocationLevelNames(data.location_level_names);
                if (data.tier) setTier(data.tier);
                if (data.primary_lang_name) setPrimaryLangName(data.primary_lang_name);
                if (data.secondary_lang_name) setSecondaryLangName(data.secondary_lang_name);
                if (data.secondary_lang_dir) setSecondaryLangDir(data.secondary_lang_dir);
            }

            if (targetLang) setLanguage(targetLang);
        } catch (e) {
            console.error("Theme fetch error:", e);
            setAvailableLanguages([{ code: 'ar', name: 'العربية', direction: 'rtl' }]);
        } finally {
            setIsThemeLoaded(true);
        }
    };

    useEffect(() => {
        fetchTheme(selectedOrgId);
    }, [selectedOrgId]);

    const refreshTheme = async () => {
        await fetchTheme(selectedOrgId);
    };

    const formatNumber = useCallback((n: number | string): string => {
        if (n === undefined || n === null) return '';
        const s = n.toString();

        const indianNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
        const westernNumerals = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

        if (numberFormat === 'indian') {
            // First, convert any existing Indian numerals to Western to normalize, then to Indian
            // This handles mixed strings or strings already in Indian numerals correctly
            let normalized = s;
            for (let i = 0; i < 10; i++) {
                normalized = normalized.split(indianNumerals[i]).join(westernNumerals[i]);
            }
            return normalized.replace(/[0-9]/g, (w) => indianNumerals[parseInt(w)]);
        } else {
            // Convert any Indian numerals to Western
            let result = s;
            for (let i = 0; i < 10; i++) {
                result = result.split(indianNumerals[i]).join(westernNumerals[i]);
            }
            return result;
        }
    }, [numberFormat]);

    const formatDate = useCallback((dateInput: Date | string | null | undefined): string => {
        if (!dateInput) return '';

        // If it's a date-only string like "YYYY-MM-DD", treat as calendar date and don't shift timezone
        if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput.trim())) {
            const trimmed = dateInput.trim();
            const parts = trimmed.split('-');
            const year = parts[0];
            const month = parts[1];
            const day = parts[2];
            
            let dateStr = '';
            if (dateFormat === 'MM/DD/YYYY') {
                dateStr = `${month}/${day}/${year}`;
            } else if (dateFormat === 'YYYY-MM-DD') {
                dateStr = trimmed;
            } else {
                dateStr = `${day}/${month}/${year}`;
            }
            return formatNumber(dateStr);
        }

        const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
        if (isNaN(d.getTime())) return '';

        try {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone || 'Asia/Damascus',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });

            const parts = formatter.formatToParts(d);
            const partMap: Record<string, string> = {};
            for (const p of parts) {
                partMap[p.type] = p.value;
            }

            const year = partMap.year || '';
            const month = partMap.month || '';
            const day = partMap.day || '';

            let dateStr = '';
            if (dateFormat === 'MM/DD/YYYY') {
                dateStr = `${month}/${day}/${year}`;
            } else if (dateFormat === 'YYYY-MM-DD') {
                dateStr = `${year}-${month}-${day}`;
            } else {
                dateStr = `${day}/${month}/${year}`;
            }

            return formatNumber(dateStr);
        } catch (e) {
            console.error("formatDate error:", e);
            return formatNumber(d.toLocaleDateString());
        }
    }, [dateFormat, timezone, formatNumber]);

    const formatDateTime = useCallback((dateInput: Date | string | null | undefined): string => {
        if (!dateInput) return '';

        const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
        if (isNaN(d.getTime())) return '';

        try {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone || 'Asia/Damascus',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });

            const parts = formatter.formatToParts(d);
            const partMap: Record<string, string> = {};
            for (const p of parts) {
                partMap[p.type] = p.value;
            }

            const year = partMap.year || '';
            const month = partMap.month || '';
            const day = partMap.day || '';
            const hour = partMap.hour || '';
            const minute = partMap.minute || '';

            let dateStr = '';
            if (dateFormat === 'MM/DD/YYYY') {
                dateStr = `${month}/${day}/${year}`;
            } else if (dateFormat === 'YYYY-MM-DD') {
                dateStr = `${year}-${month}-${day}`;
            } else {
                dateStr = `${day}/${month}/${year}`;
            }

            let timeStr = '';
            if (timeFormat === '12h') {
                let hr = parseInt(hour, 10);
                const ampm = hr >= 12 ? 'PM' : 'AM';
                hr = hr % 12;
                hr = hr ? hr : 12;
                const padHr = hr.toString().padStart(2, '0');
                timeStr = ` ${padHr}:${minute} ${ampm}`;
            } else {
                timeStr = ` ${hour}:${minute}`;
            }

            return formatNumber(dateStr + timeStr);
        } catch (e) {
            console.error("formatDateTime error:", e);
            return formatNumber(d.toLocaleString());
        }
    }, [dateFormat, timezone, timeFormat, formatNumber]);

    useEffect(() => {
        const isEnglish = language === 'primary' || language === 'en';
        const dir = isEnglish ? 'ltr' : secondaryLangDir;
        document.documentElement.dir = dir;
        document.documentElement.lang = isEnglish ? 'en' : language;
    }, [language, secondaryLangDir]);

    useEffect(() => {
        if (colorMode === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [colorMode]);

    useEffect(() => {
        setSearchQuery('');
    }, [location.pathname]);

    useEffect(() => {
        const publicRoutes = ['/', '/login', '/register', '/pricing'];
        const isPublicRoute = publicRoutes.includes(location.pathname);

        if (isPublicRoute) {
            // Remove custom organization branding on public pages
            document.documentElement.style.removeProperty('--color-primary-500');
            document.documentElement.style.removeProperty('--color-primary-600');
            document.documentElement.style.removeProperty('--color-secondary-500');
            document.documentElement.style.removeProperty('--color-secondary-600');
            document.documentElement.style.removeProperty('--font-family-active');
        } else {
            // Re-apply custom organization branding inside the app
            if (customColors.primary) {
                document.documentElement.style.setProperty('--color-primary-500', customColors.primary);
                document.documentElement.style.setProperty('--color-primary-600', customColors.primary + 'dd');
            }
            if (customColors.secondary) {
                document.documentElement.style.setProperty('--color-secondary-500', customColors.secondary);
                document.documentElement.style.setProperty('--color-secondary-600', customColors.secondary + 'dd');
            }
            if (customColors.fontFamily) {
                document.documentElement.style.setProperty('--font-family-active', `"${customColors.fontFamily}"`);
            }
        }
    }, [location.pathname, customColors]);

    if (!isThemeLoaded) {
        const isLandingPage = location.pathname === '/';
        const publicRoutes = ['/', '/login', '/register', '/pricing', '/faq', '/terms', '/privacy', '/contact'];
        const isPublicRoute = publicRoutes.includes(location.pathname);

        if (isPublicRoute) {
            return (
                <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 font-sans transition-colors duration-300">
                    {/* Header Skeleton */}
                    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md border-b border-neutral-100 dark:border-neutral-800/50">
                        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                            {/* Logo */}
                            <div className="h-9 w-32 bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                            {/* Nav Links */}
                            <div className="hidden md:flex items-center gap-8">
                                <div className="h-4 w-16 bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                                <div className="h-4 w-16 bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                                <div className="h-4 w-16 bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                                <div className="h-4 w-16 bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                            </div>
                            {/* Actions */}
                            <div className="flex items-center gap-4">
                                <div className="h-9 w-9 bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                                <div className="h-9 w-9 bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                                <div className="h-9 w-24 bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                            </div>
                        </div>
                    </header>

                    {isLandingPage ? (
                        <>
                            {/* Hero Section Skeleton */}
                            <section className="pt-32 pb-20 px-6 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                                <div className="lg:col-span-7 space-y-6">
                                    <div className="h-4 w-28 bg-neutral-200 dark:bg-neutral-800 rounded-full animate-pulse" />
                                    <div className="space-y-3">
                                        <div className="h-10 w-full bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                                        <div className="h-10 w-5/6 bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="h-4 w-full bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                                        <div className="h-4 w-4/5 bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                                    </div>
                                    <div className="flex flex-wrap gap-4 pt-4">
                                        <div className="h-12 w-36 bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                                        <div className="h-12 w-36 bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                                    </div>
                                </div>
                                <div className="lg:col-span-5">
                                    <div className="aspect-4/3 w-full bg-neutral-200 dark:bg-neutral-800 rounded-3xl animate-pulse" />
                                </div>
                            </section>

                            {/* Features Section Skeleton */}
                            <section className="py-20 bg-white dark:bg-neutral-900/50 border-y border-neutral-100 dark:border-neutral-800/50">
                                <div className="max-w-7xl mx-auto px-6 space-y-12">
                                    <div className="text-center space-y-4 max-w-2xl mx-auto">
                                        <div className="h-8 w-64 bg-neutral-200 dark:bg-neutral-800 rounded-xl mx-auto animate-pulse" />
                                        <div className="h-4 w-80 bg-neutral-200 dark:bg-neutral-800 rounded-lg mx-auto animate-pulse" />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                        {[1, 2, 3, 4, 5, 6].map((i) => (
                                            <div key={i} className="p-6 border border-neutral-100 dark:border-neutral-800/50 rounded-2xl space-y-4">
                                                <div className="h-10 w-10 bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                                                <div className="h-6 w-40 bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                                                <div className="space-y-2">
                                                    <div className="h-3 w-full bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
                                                    <div className="h-3 w-5/6 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </section>
                        </>
                    ) : (
                        /* Generic Public Page Skeleton */
                        <main className="pt-32 pb-20 px-6 max-w-4xl mx-auto space-y-8">
                            <div className="h-10 w-64 bg-neutral-200 dark:bg-neutral-800 rounded-xl animate-pulse" />
                            <div className="space-y-4">
                                <div className="h-4 w-full bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                                <div className="h-4 w-11/12 bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                                <div className="h-4 w-4/5 bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse" />
                            </div>
                            <div className="h-64 w-full bg-neutral-200 dark:bg-neutral-800 rounded-3xl animate-pulse" />
                        </main>
                    )}
                </div>
            );
        }

        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 dark:bg-neutral-900 gap-5 transition-colors duration-300">
                <div className="relative w-20 h-20 flex items-center justify-center">
                    <div className="absolute inset-0 bg-primary-500/10 rounded-3xl animate-ping opacity-75" />
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 rounded-3xl blur-xl opacity-20 animate-pulse" />
                    <div className="relative w-16 h-16 bg-white border border-neutral-100/60 rounded-2xl flex items-center justify-center shadow-lg p-2.5 animate-pulse">
                        <Logo className="w-full h-full" showBackground={false} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <ThemeContext.Provider value={{ 
            theme, toggleTheme, colorMode, toggleColorMode, language, toggleLanguage, 
            refreshTheme, numberFormat, formatNumber,
            timeFormat, dateFormat, timezone, locationLevelNames, t,
            primaryLangName, secondaryLangName, secondaryLangDir,
            availableLanguages, changeLanguage, formatDate, formatDateTime,
            searchQuery, setSearchQuery, customColors,
            showFloatingButtons, toggleFloatingButtons
        }}>
            <div className={`min-h-screen font-sans ${theme === 'admin' ? 'bg-primary-50' : 'bg-neutral-50'}`}>
                {children}
            </div>
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) throw new Error('useTheme must be used within a ThemeProvider');
    return context;
};
